import { useContext, useEffect, useState } from "react";
import { getCollectionRequest } from "services/apiRequests";
import {
  Course,
  Program,
  Project,
  Module,
  QuizListType,
} from "utils/interfaces";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import QuizListCard from "./QuizListCard";
import { MainContext } from "components/utils/Contexts";
import { logoutFunction } from "services/authRequests";

interface QuizzesListProps {
  searchQuery: string;
  selectedData?: Program | Course | Project | Module | null;
  navRegister?: {
    lessonListToShow: string;
    selectedData: Program | Course | Project | Module | null;
    record: Program | Course | Project | Module | null;
  }[];
  setShowEditQuiz?: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedQuizToEdit?: React.Dispatch<
    React.SetStateAction<QuizListType | null>
  >;
  showEditQuiz?: boolean;
}

const QuizzesList: React.FC<QuizzesListProps> = ({
  searchQuery,
  selectedData,
  navRegister,
  setShowEditQuiz,
  setSelectedQuizToEdit,
}) => {
  // States definition
  const [data, setData] = useState<QuizListType[]>([]);

  const { userRole } = useContext(MainContext);

  // Define a navigator state
  const navigate = useNavigate();
  const { state } = useLocation();

  /**
   * Root definition
   */
  useEffect(() => {
    if (selectedData && "_id" in selectedData) {
      getData(selectedData._id);
    }
  }, [selectedData]);

  /**
   * Function to get the data depending on the class
   * @returns data
   */
  const getData = async (courseId: string | undefined = undefined) => {
    // Check if current course is defined
    if (!courseId) return;

    let url = "/api/quizzes";
    if (["class admin", "customer admin", "master admin"].includes(userRole)) {
      url = "/api/quizzes/full"; // include quiz answers in response for admins
    }

    // Get the quizzes
    const response = await getCollectionRequest(`${url}?courseId=${courseId}`);

    // Check if the response is successful
    if (!response.successful) {
      // Check if it's not successful because of a token expiration
      if (response.logout) {
        // alert(response.message);
        return await logoutFunction();
      }

      return [];
    }

    // Get the data
    const data = response.content;

    // For each quiz, add the badgeImage URL
    const processedData = data.map((quiz: QuizListType) => {
      // Adding the badge image
      quiz.badgeImage = `/api/imgs/quizzes/${quiz._id}/badgeImage`;
      return quiz;
    });

    // Assign the data to the corresponding element
    setData(processedData);
    return processedData;
  };

  /**
   * Function applied when we press the edit button on cards
   */
  const handleCardClick = (record: QuizListType, selectedTab: number) => {
    if (selectedTab === 0) {
      if (navRegister !== undefined) {
        navigate(`/quiz/${record._id}`, {
          state: {
            activeItem: "lessons",
            activeLesson: selectedData?._id,
            navRegister: navRegister,
          },
        });
      } else {
        navigate(`/quiz/${record._id}`, { state });
      }
    }
  };

  /**
   * Expand click definition
   */
  const handleExpandClick = (record: QuizListType) => {
    return (event: React.MouseEvent) => {
      event.stopPropagation();
      handleCardClick(record, 0);
    };
  };

  /**
   * Edit click definition
   */
  const handleEditClick = (record: QuizListType) => {
    return (event: React.MouseEvent) => {
      event.stopPropagation();
      setShowEditQuiz && setShowEditQuiz(true);
      setSelectedQuizToEdit && setSelectedQuizToEdit(record);
    };
  };

  /**
   * Function that renders the list of cards depending of the activeClass selected
   * @returns a list of cards
   */
  const cardsToRender = () => {
    return (
      data &&
      data.length > 0 &&
      data
        .filter(
          (item: QuizListType) =>
            item.active &&
            item.title.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .map((quiz: any, index: number) => (
          <QuizListCard
            key={index}
            quiz={quiz}
            handleExpand={handleExpandClick(quiz)}
            handleEdit={handleEditClick(quiz)}
          />
        ))
    );
  };

  return (
    <>
      {data && data.length > 0 && (
        <AnimatePresence>
          <motion.div className="w-full h-full inline-flex flex-col gap-3">
            {cardsToRender()}
          </motion.div>
        </AnimatePresence>
      )}
    </>
  );
};

export default QuizzesList;
